import { commandsStore } from "@/store/commandsStore";
import { gptStore } from "@/store/gptStore";
import {
	isAuctionController,
	isCarouselStart,
	isGPT,
	isGPTRefresh,
	isNavStart,
	isSlotDiscovery,
	isStartCommand,
} from "@/ts/guards";
import type {
	SequenceControllerModule,
	SequenceModuleConfig,
} from "@/ts/module";
import type { Exactly, SlotTargeting, StartCommand } from "@/ts/types";
import { getModules } from "@/utils/modules";

export class SequenceController
	implements Exactly<SequenceControllerModule, SequenceController>
{
	private config: SequenceModuleConfig;
	name = "SequenceController" as const;

	constructor(config: SequenceModuleConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		const { selectStartCommand } = commandsStore.getState().selectors;
		const { actions } = gptStore.getState();

		commandsStore.subscribe(selectStartCommand, (selectedCmd) => {
			if (selectedCmd) {
				if (isNavStart(selectedCmd)) {
					const { id, slots } = selectedCmd;
					// 'carousel' start command expects SlotDisco to create slotConfigs and add to store.
					// 'nav' start command has slotConfigs included, and doesn't use SlotDisco, so add them now.
					slots && actions.setSlotConfigsByCommandId(id, slots);
				}
				this.run(selectedCmd);
			}
		});

		await Promise.resolve();
	}

	async run(command: StartCommand) {
		if (!(command && isStartCommand(command))) {
			return;
		}

		const { promise, resolve } = Promise.withResolvers<
			Record<string, SlotTargeting> | undefined
		>();

		let auctionTargeting: Record<string, SlotTargeting> | undefined;

		//TODO: NC2-1325 - Remove the default 'carousel' label after the carousel repo is released with support for sequence label
		const { sequence: sequenceLabel = "carousel", id: commandId } = command;

		const moduleNames = this.config[sequenceLabel];
		const modules = await getModules(moduleNames);

		for (const mod of modules) {
			if (isSlotDiscovery(mod) && isCarouselStart(command) && command.doc) {
				await mod.run(commandId, command.doc);
			}
			if (isAuctionController(mod)) {
				const { selectors } = gptStore.getState();
				// pass undefined to let AuctionController choose the default timeout value
				mod.run(
					selectors.getSlotConfigsByCommandId(commandId),
					resolve,
					undefined,
				);
				auctionTargeting = await promise;
			}
			if (isGPT(mod)) {
				await mod.run(commandId, auctionTargeting);
			}
			if (isGPTRefresh(mod) && isNavStart(command) && command.slots) {
				mod.run(commandId, command.slots);
			}
		}
		return Promise.resolve(true);
	}
}
